import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { AuthService } from '../features/auth/services/authService';
import { useAuthStore } from '../features/auth/stores/authStore';

/**
 * OAuth callback page that handles the WorkOS authentication callback
 */
export function AuthCallbackPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  const { setUser } = useAuthStore();

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const error = urlParams.get('error');
        const code = urlParams.get('code');

        if (error) {
          setStatus('error');
          setError(decodeURIComponent(error));
          return;
        }

        if (code) {
          const result = await AuthService.handleCallback();
          if (result.success) {
            const userProfile = await AuthService.getProfile();
            setUser(userProfile);
            setStatus('success');

            // Add delay to ensure state is set before navigation
            setTimeout(() => {
              navigate('/my-families', { replace: true });
            }, 500);
          } else {
            setStatus('error');
            if (result.error?.includes('adult_not_found') || result.error?.includes('Adult not found')) {
              setError('Access not available. Please contact the administrator to set up your account.');
            } else {
              setError(result.error || 'Authentication failed');
            }
          }
          return;
        }

        setStatus('error');
        setError('Invalid authentication callback - missing authorization code');
      } catch (err) {
        console.error('AuthCallbackPage: Exception during callback:', err);
        setStatus('error');
        setError(err instanceof Error ? err.message : 'Authentication process failed');
      }
    };

    handleCallback();
  }, [navigate, setUser]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="max-w-md w-full space-y-8 text-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Completing login...</h2>
            <p className="mt-2 text-sm text-gray-600">Please wait while we complete your authentication.</p>
          </div>

          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>

          <div className="mt-8">
            <button
              onClick={() => {
                navigate('/my-families', { replace: true });
              }}
              className="text-sm text-blue-600 hover:text-blue-500 underline"
            >
              Taking too long? Click here to continue
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="max-w-md w-full space-y-8 text-center">
          <div>
            <div className="mx-auto h-12 w-12 text-green-600">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mt-4">Login successful!</h2>
            <p className="mt-2 text-sm text-gray-600">Redirecting to your families...</p>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="max-w-md w-full space-y-8 text-center">
          <div>
            <div className="mx-auto h-12 w-12 text-red-600">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mt-4">Access Unavailable</h2>
            <p className="mt-2 text-sm text-gray-600">
              {error || 'Authentication failed. Please try again or contact support if the issue persists.'}
            </p>
          </div>

          <div>
            <button
              onClick={() => AuthService.login()}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Try Again
            </button>
          </div>

          {/* Contact Information */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <h3 className="text-sm font-medium text-gray-900 mb-4">Need Help?</h3>
            <div className="space-y-3 text-sm text-gray-600">
              <div className="flex items-center justify-center space-x-2">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
                <span>(506) 384-7874</span>
              </div>
              <div className="flex items-center justify-center space-x-2">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                <span>86 Rue Ste Therese, Dieppe, NB E1A 1S7</span>
              </div>
              <div className="flex items-center justify-center space-x-2">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span>Mon-Fri: 8:30 AM - 4:30 PM</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
